import { useState } from "react";
import { IconCheckCircle } from "../../../../../common/icons/IconCheckCircle/IconCheckCircle";
import { LeafIconButton } from "../../../../../common/leaf/LeafIconButton/LeafIconButton";
import LeafTile from "../../../../../common/leaf/LeafTile/LeafTile";
import defaultContent from "../../../../../content/keyMetrics";
import defaultAwardContent from "../../../../../content/awardLabels";
import { getCountryNameFromCode } from "../../../../../common/util/formatCountryName";
import { storedLocale } from "../../../../Authentication/util";
import "./PerfIncomeKpiItemTile.scss";
import { useTranslation } from "react-i18next";
import { IconArrowUp } from "../../../../../common/icons/IconArrowUp/IconArrowUp";
import { IconArrowDropdown } from "../../../../../common/icons/IconArrowDropdown/IconArrowDropdown";
import { IconInfoCircle } from "../../../../../common/icons/IconInfoCircle/IconInfoCircle";
import { KpiInfoModal } from "../../../../../common/components/KpiSelector/KpiInfoModal/KpiInfoModal";
import { useAppSelector } from "../../../../../store";
import { maskPerfIncomeKpiItemData } from "./PerfIncomeKpiItemTile.mask";

export interface KpiCountryVolume {
  value: number;
  formattedValue: string;
}

export interface KpiVolumeByCountry {
  [countryCode: string]: KpiCountryVolume;
}

export const PV_TYPE_MAP: Record<string, string> = {
  "001": "ppvTarget",
  "925": "vcspvTarget",
  "007": "pcpvTarget",
};

export interface PerfIncomeKpiItemProps {
  label: string;
  value: number;
  formattedValue: string;
  volumeByCountry?: KpiVolumeByCountry;
  isMet?: boolean;
  volumeCode?: string;
  showPVBreakdown?: boolean;
}

export default function PerfIncomeKpiItemTile(props: PerfIncomeKpiItemProps) {
  const { t } = useTranslation(["keyMetrics", "awardLabels"]);
  const [open, setOpen] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const locale = storedLocale();
  const showData = useAppSelector((state) => state.showData.showData);
  const noDataLabel = t(
    "awardLabels.no-data-available",
    defaultAwardContent.awardLabels["no-data-available"] as string,
  );

  const { label, formattedValue, volumeByCountry, isMet, volumeCode, showPVBreakdown } = maskPerfIncomeKpiItemData(
    props,
    showData,
  );

  const canExpand = !!showPVBreakdown && !!volumeByCountry && Object.keys(volumeByCountry).length > 1;

  const titleKey = volumeCode ? PV_TYPE_MAP[volumeCode] : label;

  const hasInfoModal = (): boolean => {
    return defaultContent[`${titleKey}ModalTitle`] !== undefined;
  };

  const hasSubtext = (): boolean => {
    return defaultContent[`${titleKey}Subtext`] !== undefined;
  };

  return (
    <>
      <LeafTile className={`PerfIncomeKpiItemTile ${open ? "is-open" : ""}`}>
        <div className="PerfIncomeKpiItemTile__content">
          <div className="PerfIncomeKpiItemTile__text">
            <div className="PerfIncomeKpiItemTile__label-wrapper">
              <p className="PerfIncomeKpiItemTile__label" data-testid={`perf-kpi-label-${titleKey}`}>
                {t(`${titleKey}`, defaultContent[titleKey] || titleKey)}
              </p>
              {hasInfoModal() && (
                <LeafIconButton
                  onClick={() => setShowInfoModal(true)}
                  Icon={<IconInfoCircle />}
                  aria-label={`Info about ${t(`${titleKey}`, defaultContent[titleKey] || titleKey)}`}
                  className="PerfIncomeKpiItemTile__info-icon"
                />
              )}
            </div>
            {hasSubtext() && (
              <p className="PerfIncomeKpiItemTile__subtext">
                {t(`${titleKey}Subtext`, defaultContent[`${titleKey}Subtext`])}
              </p>
            )}
            <span
              className="PerfIncomeKpiItemTile__value"
              {...(!formattedValue || formattedValue === "--" ? { role: "text", "aria-label": noDataLabel } : {})}
            >
              {!formattedValue || formattedValue === "--" ? (
                <span aria-hidden="true">{formattedValue || "--"}</span>
              ) : (
                formattedValue
              )}
            </span>
          </div>

          <div className="PerfIncomeKpiItemTile__icon">
            {canExpand ? (
              <div className="PerfIncomeKpiItemTile__arrow">
                <LeafIconButton
                  onClick={() => setOpen(!open)}
                  Icon={
                    open ? (
                      <IconArrowUp backgroundClass="blue" size="lg" />
                    ) : (
                      <IconArrowDropdown backgroundClass="black" size="lg" />
                    )
                  }
                />
              </div>
            ) : (
              isMet !== undefined && (
                <div className="PerfIncomeKpiItemTile__check">
                  <IconCheckCircle color={isMet === true ? "green" : "gray"} size="md" solidCheckCircle={isMet} />{" "}
                </div>
              )
            )}
          </div>
        </div>

        {open && canExpand && (
          <div className="PerfIncomeKpiItemTile__countries">
            {Object.entries(volumeByCountry!).map(([code, data]) => (
              <div key={code} className="PerfIncomeKpiItemTile__country">
                <span>- {getCountryNameFromCode(code, locale)}</span>
                <span className="PerfIncomeKpiItemTile__pv">{data.formattedValue}</span>
              </div>
            ))}
          </div>
        )}
      </LeafTile>
      {showInfoModal && <KpiInfoModal open={true} onClose={() => setShowInfoModal(false)} kpi={titleKey} />}
    </>
  );
}
